import React, { useState } from 'react'
import {
  ConditionalWrapper,
  Tooltip,
  Separator,
  CopyToClipboard,
} from '@sketch/components'

import {
  CollapseIcon,
  TitleAndActionsWrapper,
  CollapsibleContainer,
  Title,
  WarningsContainer,
  ActionsWrapper,
  LeftWrapper,
  Trigger,
  ActionButton,
} from './CollapsibleInspectorSection.styles'
import * as Collapsible from '@radix-ui/react-collapsible'

interface Action {
  icon: any
  tooltipText?: string
  onClick: () => void
}

interface CollapsibleSectionProps {
  className?: string
  'data-testid'?: string
  id?: string
  title?: string | React.ReactNode
  actions?: Action[]
  children?: React.ReactNode
  iconWarnings?: React.ReactNode
  noPadding?: boolean
  noMarginBottom?: boolean
}

const getId = (id?: string) => {
  if (!id) return undefined
  return { id }
}

export const CollapsibleInspectorSection = React.forwardRef<
  HTMLDivElement,
  CollapsibleSectionProps
>(function CollapsibleSection(
  {
    className,
    children,
    title,
    'data-testid': dataTestId,
    id,
    actions,
    iconWarnings,
    noPadding,
    noMarginBottom,
  },
  ref
) {
  const [isOpen, setIsOpen] = useState(true)
  const [tooltipVisibleActionId, setTooltipVisibleActionId] = useState<
    number | undefined
  >(undefined)

  const handleActionClick = (action: Action, id: number) => {
    action.onClick()

    if (!action.tooltipText) {
      return
    }
    setTooltipVisibleActionId(id)

    window.setTimeout(() => {
      setTooltipVisibleActionId(undefined)
    }, CopyToClipboard.animationDuration)
  }

  return (
    <Collapsible.Root
      ref={ref}
      className={className}
      data-testid={dataTestId}
      open={isOpen}
      onOpenChange={setIsOpen as (isOpen: boolean) => void}
      {...getId(id)}
    >
      <TitleAndActionsWrapper $isCollapsed={!isOpen}>
        <LeftWrapper>
          <Title>{title}</Title>
        </LeftWrapper>
        <ActionsWrapper>
          {iconWarnings ? (
            <WarningsContainer>{iconWarnings}</WarningsContainer>
          ) : null}
          {actions?.map((action, index) => (
            <ConditionalWrapper
              key={`action-${index}`}
              condition={!!action.tooltipText}
              wrapper={children => (
                <Tooltip
                  className="tooltip"
                  visible={tooltipVisibleActionId === index}
                  placement="top"
                  content={action.tooltipText}
                >
                  {children}
                </Tooltip>
              )}
            >
              <ActionButton onClick={() => handleActionClick(action, index)}>
                {action.icon}
              </ActionButton>
            </ConditionalWrapper>
          ))}

          <Trigger asChild>
            <button>
              <CollapseIcon $isCollapsed={!isOpen} />
            </button>
          </Trigger>
        </ActionsWrapper>
      </TitleAndActionsWrapper>
      <Collapsible.Content data-testid="documents-content">
        <CollapsibleContainer
          $isCollapsed={!isOpen}
          noPadding={noPadding}
          noMarginBottom={noMarginBottom}
        >
          {children}
        </CollapsibleContainer>
      </Collapsible.Content>
      <Separator />
    </Collapsible.Root>
  )
})
